import React, { useState, useEffect, startTransition } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Globe, ChevronDown } from 'react-feather';
import LogoIcon from '../ui/LogoIcon';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import { trackEvent, trackConversion } from '../../services/trackingService';
import { useData } from '../../context/DataContext';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../ui/LocalizedLink';
import { AnimatePresence, motion } from 'framer-motion';

const LanguageSwitcher: React.FC = () => {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const languages = {
        en: 'English',
        es: 'Español'
    };

    const changeLanguage = (lng: string) => {
        // Prevent unnecessary navigation if the language is already selected
        if (i18n.language === lng) {
            setIsOpen(false);
            return;
        }

        let newPath = location.pathname;
        
        // If the current path starts with the current language prefix, replace it
        if (newPath.startsWith(`/${i18n.language}`)) {
            newPath = newPath.replace(`/${i18n.language}`, `/${lng}`);
        } else {
            // Otherwise, it's an English URL without a prefix.
            // If we are switching to English, we don't need a prefix.
            // If we are switching to another language, we add the prefix.
            if (lng !== 'en') {
                newPath = `/${lng}${newPath === '/' ? '' : newPath}`;
            }
        }
        
        // If switching back to English, and the path has /en, remove it
        if (lng === 'en' && newPath.startsWith('/en')) {
            newPath = newPath.replace('/en', '') || '/';
        }

        // Use client-side navigation. This will update the URL, and the `LanguageWrapper`
        // component will handle the actual language state change based on the new URL param.
        // This provides a smoother user experience without a full page reload.
        startTransition(() => {
            navigate(newPath);
        });
        
        setIsOpen(false);
    };

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1 text-sm text-white/90 hover:text-white"
                aria-label="Change language"
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <Globe size={16} />
                <span>{languages[i18n.language as keyof typeof languages]}</span>
                <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full right-0 mt-2 w-32 bg-brand-dark/90 border border-white/10 rounded-lg shadow-lg glass-effect"
                    >
                        {Object.keys(languages).map((lng) => (
                            <button
                                key={lng}
                                onClick={() => changeLanguage(lng)}
                                className={`w-full text-left px-4 py-2 text-sm ${i18n.language === lng ? 'font-bold text-brand-gold-light' : 'text-white/80 hover:bg-white/10'}`}
                            >
                                {languages[lng as keyof typeof languages]}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const { firmInfo } = useData();
  const { t } = useTranslation('common');

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.practiceAreas'), path: '/practice-areas' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.results'), path: '/results' },
    { name: t('nav.reviews'), path: '/reviews' },
    { name: t('nav.blog'), path: '/blog' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  useEffect(() => {
    if (typeof window !== 'undefined') {
      let ticking = false;
      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            setIsScrolled(window.scrollY > 10);
            ticking = false;
          });
          ticking = true;
        }
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handlePhoneClick = () => {
    trackEvent('click_to_call', { location: 'navbar' });
    trackConversion('Contact', { content_name: 'click_to_call', location: 'navbar' });
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/80 border-b border-white/10 glass-effect' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
          <LocalizedLink to="/" className="flex items-center gap-3 group" aria-label="The Action Firm Home">
            <LogoIcon className="h-10 w-10 transition-transform duration-300 group-hover:scale-110" />
            <span className="text-sm font-semibold tracking-widest">THE <span className="text-brand-gold-light">ACTION</span> FIRM</span>
          </LocalizedLink>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <LocalizedLink
                key={link.name}
                to={link.path}
                isNavLink
              >
                {link.name}
              </LocalizedLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            {isAuthenticated ? (
               <Button onClick={logout} variant="ghost">{t('nav.logout')}</Button>
            ) : (
                <>
                <a href={`tel:${firmInfo.telephone}`} onClick={handlePhoneClick} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-4 py-2 text-xs font-medium glass-effect hover:bg-white/15 transition-colors">
                  <Phone className="h-4 w-4 text-brand-gold-light" /> {firmInfo.displayPhone}
                </a>
                 <LocalizedLink to="/client-portal" className="text-sm text-white/90 hover:text-white hover:[text-shadow:0_0_8px_rgba(233,200,74,0.9),_0_0_20px_rgba(205,175,42,0.6)] transition-all duration-300">{t('nav.clientPortal')}</LocalizedLink>
                </>
            )}
          </div>

          <div className="lg:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white" aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')} aria-expanded={isMenuOpen}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`fixed top-20 left-0 w-full h-[calc(100vh-80px)] bg-black/95 glass-effect z-40 transition-transform duration-300 ease-in-out lg:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <nav className="flex flex-col items-center justify-center h-full gap-8">
          {/* Moved language switcher to the top for better mobile UX */}
          <div>
            <LanguageSwitcher />
          </div>
          {navLinks.map((link) => (
            <LocalizedLink
              key={link.name}
              to={link.path}
              onClick={() => setIsMenuOpen(false)}
              isNavLink
              className="text-2xl"
            >
              {link.name}
            </LocalizedLink>
          ))}
          {isAuthenticated ? (
             <Button onClick={() => { logout(); setIsMenuOpen(false); }} variant="secondary" className="mt-8">{t('nav.logout')}</Button>
          ) : (
            <>
            <a href={`tel:${firmInfo.telephone}`} onClick={handlePhoneClick} className="mt-8 flex items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-6 py-3 text-lg font-medium glass-effect hover:bg-white/15 transition-colors">
                <Phone className="h-5 w-5 text-brand-gold-light" /> {firmInfo.displayPhone}
            </a>
            <LocalizedLink to="/client-portal" onClick={() => setIsMenuOpen(false)} className="mt-4 text-lg text-white/90 hover:text-white hover:[text-shadow:0_0_8px_rgba(233,200,74,0.9),_0_0_20px_rgba(205,175,42,0.6)] transition-all duration-300">{t('nav.clientPortal')}</LocalizedLink>
            </>
          )}
        </nav>
      </div>
    </>
  );
};

export default Navbar;